import { useState } from 'react';
import { Badge, Button, Card, CardDescription, CardHeader, CardTitle, Checkbox, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Label, Switch, toast } from '@ludolio/shared-ui';
import { Loader2 } from 'lucide-react';
import { useUnitsModule } from '../../module-context';
import type { Unit, UnitLabsSettings } from '../../types';

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const candidate = error as { response?: { data?: { message?: unknown; error?: unknown } }; message?: unknown };
    if (typeof candidate.response?.data?.message === 'string') return candidate.response.data.message;
    if (typeof candidate.response?.data?.error === 'string') return candidate.response.data.error;
    if (typeof candidate.message === 'string') return candidate.message;
  }
  return fallback;
}

function publishedGamesLabel(count: number) {
  return count === 1 ? '1 published game' : `${count} published games`;
}

interface UnitLabsCardProps {
  unit: Unit;
  settings: UnitLabsSettings;
  onSettingsChanged(settings: UnitLabsSettings): void;
}

export function UnitLabsCard({ unit, settings, onSettingsChanged }: UnitLabsCardProps) {
  const { api, reportError } = useUnitsModule();
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [takeDown, setTakeDown] = useState(false);
  const canManage = settings.canManage && Boolean(api.updateUnitLabsSettings);

  const save = async (enabled: boolean, takeDownPublished: boolean) => {
    if (!api.updateUnitLabsSettings) return;
    setSaving(true);
    try {
      const response = await api.updateUnitLabsSettings(unit.id, { enabled, takeDownPublished });
      if (!response.success || !response.data) throw new Error(response.error || response.message || 'Failed to update Labs');
      onSettingsChanged(response.data);
      setConfirmOpen(false);
      toast.success(enabled ? 'Labs turned on' : takeDownPublished ? 'Labs turned off and games taken down' : 'Labs turned off');
    } catch (error) {
      reportError(error);
      toast.error(getErrorMessage(error, 'Failed to update Labs'));
    } finally {
      setSaving(false);
    }
  };

  const onToggle = (checked: boolean) => {
    if (checked) {
      void save(true, false);
    } else if (settings.publishedCount > 0) {
      setTakeDown(false);
      setConfirmOpen(true);
    } else {
      void save(false, false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-4 space-y-0">
          <div className="min-w-0 space-y-1.5">
            <CardTitle as="h2" id={`unit-labs-title-${unit.id}`} className="text-base leading-6">
              Enable Ludolio Labs for this unit
            </CardTitle>
            <CardDescription id={`unit-labs-description-${unit.id}`}>Students can create and submit projects</CardDescription>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />}
            {canManage ? (
              <Switch
                id={`unit-labs-${unit.id}`}
                aria-labelledby={`unit-labs-title-${unit.id}`}
                aria-describedby={`unit-labs-description-${unit.id}`}
                checked={settings.enabled}
                disabled={saving}
                onCheckedChange={onToggle}
              />
            ) : (
              <Badge variant={settings.enabled ? 'success' : 'secondary'}>{settings.enabled ? 'On' : 'Off'}</Badge>
            )}
          </div>
        </CardHeader>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={(open) => !saving && setConfirmOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Turn off Labs for {unit.code}?</DialogTitle>
            <DialogDescription>Students won't be able to create or submit projects in this unit.</DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 pt-2">
            <Checkbox
              id={`unit-labs-takedown-${unit.id}`}
              checked={takeDown}
              onCheckedChange={(checked) => setTakeDown(checked === true)}
              disabled={saving}
            />
            <Label htmlFor={`unit-labs-takedown-${unit.id}`} className="cursor-pointer pt-1 font-normal leading-5">
              Also take down {publishedGamesLabel(settings.publishedCount)}
            </Label>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant={takeDown ? 'destructive' : 'default'} onClick={() => void save(false, takeDown)} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {takeDown ? 'Turn off and take down' : 'Turn off'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
